import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  FieldHint,
  Input,
  Label,
  Alert,
  AlertDescription,
  Badge,
  useToast,
} from "@repo/ui";
import { ShieldCheck, Loader2, Lock, AlertTriangle } from "lucide-react";
import { DateInput } from "@/components/forms";
import { useApproval } from "@/context/approval-context";
import { handlePermissionError } from "@/lib/utils/handle-permission-error";
import { updatePharmacyDetails } from "@/lib/api/pharmacy";
import { usePharmacyStore } from "@/lib/stores/pharmacy-store";
import { ApiClientError } from "@/lib/api/client";

interface PharmacyData {
  ncpdp_id: string | null;
  dea_registration_number: string | null;
  dea_expiration_date: string | null;
  state_license_number: string;
  license_expiration_date: string;
}

interface LegalComplianceFormProps {
  pharmacy: PharmacyData;
  canEditSettings: boolean;
  onSuccess: () => void;
}

function isExpiringSoon(dateStr: string | null): boolean {
  if (!dateStr) return false;
  const expDate = new Date(dateStr);
  const today = new Date();
  const thirtyDaysFromNow = new Date();
  thirtyDaysFromNow.setDate(today.getDate() + 30);
  return expDate <= thirtyDaysFromNow && expDate >= today;
}

function isExpired(dateStr: string | null): boolean {
  if (!dateStr) return false;
  const expDate = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return expDate < today;
}

function formatDisplayDate(dateStr: string | null): string {
  if (!dateStr) return "Not set";
  return new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function LegalComplianceForm({
  pharmacy,
  canEditSettings,
  onSuccess,
}: LegalComplianceFormProps) {
  const { toast } = useToast();
  const { requestApproval } = useApproval();
  const invalidatePharmacy = usePharmacyStore((s) => s.invalidatePharmacy);
  const fetchPharmacyStore = usePharmacyStore((s) => s.fetchPharmacy);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const [ncpdp, setNcpdp] = useState(pharmacy.ncpdp_id || "");
  const [dea, setDea] = useState(pharmacy.dea_registration_number || "");
  const [deaExpiration, setDeaExpiration] = useState(
    pharmacy.dea_expiration_date || ""
  );
  const [license, setLicense] = useState(pharmacy.state_license_number || "");
  const [licenseExpiration, setLicenseExpiration] = useState(
    pharmacy.license_expiration_date || ""
  );

  const hasChanges =
    ncpdp !== (pharmacy.ncpdp_id || "") ||
    dea !== (pharmacy.dea_registration_number || "") ||
    deaExpiration !== (pharmacy.dea_expiration_date || "") ||
    license !== (pharmacy.state_license_number || "") ||
    licenseExpiration !== (pharmacy.license_expiration_date || "");

  const validateFields = () => {
    const errors: Record<string, string> = {};

    if (ncpdp && !/^[0-9]{7}$/.test(ncpdp)) {
      errors.ncpdp = "NCPDP must be exactly 7 digits";
    }

    if (!dea) {
      errors.dea = "DEA number is required";
    } else if (!/^[A-Z]{2}[0-9]{7}$/.test(dea.toUpperCase())) {
      errors.dea = "DEA format: 2 letters followed by 7 digits";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent, approvalToken?: string) => {
    e.preventDefault();
    if (!canEditSettings) return;

    setError("");
    setFieldErrors({});

    if (!validateFields()) {
      setError("Please fix the validation errors below");
      return;
    }

    setIsLoading(true);

    try {
      await updatePharmacyDetails(
        {
          ncpdp_id: ncpdp || null,
          dea_registration_number: dea.toUpperCase() || null,
          dea_expiration_date: deaExpiration || null,
          ...(license ? { state_license_number: license } : {}),
          ...(licenseExpiration
            ? { license_expiration_date: licenseExpiration }
            : {}),
        },
        { approvalToken }
      );

      // Refresh pharmacy store so dashboard picks up new data
      invalidatePharmacy();
      fetchPharmacyStore();

      toast({
        title: "Changes Saved",
        description:
          "Legal and compliance details have been updated successfully.",
        variant: "success",
      });
      onSuccess();
    } catch (err) {
      if (
        err instanceof ApiClientError &&
        handlePermissionError(err, {
          requestApproval,
          toast,
          retryFn: async (token) => handleSubmit(e, token),
          actionDescription: "Update compliance details",
        })
      )
        return;
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setNcpdp(pharmacy.ncpdp_id || "");
    setDea(pharmacy.dea_registration_number || "");
    setDeaExpiration(pharmacy.dea_expiration_date || "");
    setLicense(pharmacy.state_license_number || "");
    setLicenseExpiration(pharmacy.license_expiration_date || "");
    setError("");
    setFieldErrors({});
  };

  // Check for expiration warnings
  const deaExpiring = isExpiringSoon(pharmacy.dea_expiration_date);
  const deaExpired = isExpired(pharmacy.dea_expiration_date);
  const licenseExpiring = isExpiringSoon(pharmacy.license_expiration_date);
  const licenseExpired = isExpired(pharmacy.license_expiration_date);
  const hasWarnings =
    deaExpiring || deaExpired || licenseExpiring || licenseExpired;

  return (
    <div className="space-y-4">
      {/* Expiration Warnings */}
      {hasWarnings && (
        <Alert
          variant={deaExpired || licenseExpired ? "destructive" : "default"}
          className={
            !deaExpired && !licenseExpired
              ? "border-amber-200 bg-amber-50 text-amber-900"
              : ""
          }
        >
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {deaExpired && (
              <span className="block">
                Your DEA registration has expired. Please renew immediately.
              </span>
            )}
            {deaExpiring && !deaExpired && (
              <span className="block">
                Your DEA registration expires on{" "}
                {formatDisplayDate(pharmacy.dea_expiration_date)}. Consider
                renewing soon.
              </span>
            )}
            {licenseExpired && (
              <span className="block">
                Your state license has expired. Please renew immediately.
              </span>
            )}
            {licenseExpiring && !licenseExpired && (
              <span className="block">
                Your state license expires on{" "}
                {formatDisplayDate(pharmacy.license_expiration_date)}. Consider
                renewing soon.
              </span>
            )}
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500/10">
                <ShieldCheck className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <CardTitle>Legal & Compliance</CardTitle>
                <CardDescription>
                  DEA registration, state license, and pharmacy identifiers
                </CardDescription>
              </div>
            </div>
            {!canEditSettings && (
              <Badge variant="outline" className="gap-1.5">
                <Lock className="h-3 w-3" />
                Admin Only
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* DEA Section */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground">
                DEA Registration
              </h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5">
                    <Label htmlFor="dea">
                      DEA Number{" "}
                      <span className="text-destructive font-normal">*</span>
                    </Label>
                    <FieldHint>
                      Your DEA registration number. Two letters followed by
                      seven digits, for example BA1234567. Required to store and
                      dispense controlled substances.
                    </FieldHint>
                  </div>
                  {canEditSettings ? (
                    <>
                      <Input
                        id="dea"
                        placeholder="AB1234567"
                        value={dea}
                        onChange={(e) => setDea(e.target.value.toUpperCase())}
                        disabled={isLoading}
                        maxLength={9}
                        className={fieldErrors.dea ? "border-destructive" : ""}
                      />
                      {fieldErrors.dea ? (
                        <p className="text-xs text-destructive">
                          {fieldErrors.dea}
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground">
                          2 letters + 7 digits
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-sm py-2 px-3 rounded-lg bg-muted">
                      {pharmacy.dea_registration_number || "Not set"}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="deaExpiration">DEA Expiration</Label>
                  {canEditSettings ? (
                    <>
                      <DateInput
                        id="deaExpiration"
                        value={deaExpiration}
                        onChange={(v) => setDeaExpiration(v)}
                        disabled={isLoading}
                        className={
                          fieldErrors.deaExpiration ? "border-destructive" : ""
                        }
                      />
                      {fieldErrors.deaExpiration && (
                        <p className="text-xs text-destructive">
                          {fieldErrors.deaExpiration}
                        </p>
                      )}
                    </>
                  ) : (
                    <div className="flex items-center gap-2">
                      <p className="text-sm py-2 px-3 rounded-lg bg-muted flex-1">
                        {formatDisplayDate(pharmacy.dea_expiration_date)}
                      </p>
                      {deaExpired && (
                        <Badge variant="destructive" className="text-xs">
                          Expired
                        </Badge>
                      )}
                      {deaExpiring && !deaExpired && (
                        <Badge
                          variant="outline"
                          className="text-xs text-amber-600 border-amber-300"
                        >
                          Expiring Soon
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* State License Section */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground">
                State License
              </h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="license">State License Number</Label>
                  {canEditSettings ? (
                    <Input
                      id="license"
                      placeholder="License number"
                      value={license}
                      onChange={(e) => setLicense(e.target.value)}
                      disabled={isLoading}
                      className={
                        fieldErrors.license ? "border-destructive" : ""
                      }
                    />
                  ) : (
                    <p className="text-sm py-2 px-3 rounded-lg bg-muted">
                      {pharmacy.state_license_number || "Not set"}
                    </p>
                  )}
                  {fieldErrors.license && (
                    <p className="text-xs text-destructive">
                      {fieldErrors.license}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="licenseExpiration">License Expiration</Label>
                  {canEditSettings ? (
                    <>
                      <DateInput
                        id="licenseExpiration"
                        value={licenseExpiration}
                        onChange={(v) => setLicenseExpiration(v)}
                        disabled={isLoading}
                        className={
                          fieldErrors.licenseExpiration
                            ? "border-destructive"
                            : ""
                        }
                      />
                      {fieldErrors.licenseExpiration && (
                        <p className="text-xs text-destructive">
                          {fieldErrors.licenseExpiration}
                        </p>
                      )}
                    </>
                  ) : (
                    <div className="flex items-center gap-2">
                      <p className="text-sm py-2 px-3 rounded-lg bg-muted flex-1">
                        {formatDisplayDate(pharmacy.license_expiration_date)}
                      </p>
                      {licenseExpired && (
                        <Badge variant="destructive" className="text-xs">
                          Expired
                        </Badge>
                      )}
                      {licenseExpiring && !licenseExpired && (
                        <Badge
                          variant="outline"
                          className="text-xs text-amber-600 border-amber-300"
                        >
                          Expiring Soon
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* NCPDP Section */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground">
                Pharmacy Identifier
              </h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="ncpdp">
                    NCPDP Number{" "}
                    <span className="text-muted-foreground font-normal">
                      (Optional)
                    </span>
                  </Label>
                  {canEditSettings ? (
                    <>
                      <Input
                        id="ncpdp"
                        placeholder="1234567"
                        value={ncpdp}
                        onChange={(e) => setNcpdp(e.target.value)}
                        disabled={isLoading}
                        maxLength={7}
                        className={
                          fieldErrors.ncpdp ? "border-destructive" : ""
                        }
                      />
                      {fieldErrors.ncpdp ? (
                        <p className="text-xs text-destructive">
                          {fieldErrors.ncpdp}
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground">
                          7-digit pharmacy identifier
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-sm py-2 px-3 rounded-lg bg-muted">
                      {pharmacy.ncpdp_id || "Not set"}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            {canEditSettings && (
              <div className="flex items-center justify-end gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleReset}
                  disabled={isLoading || !hasChanges}
                >
                  Discard Changes
                </Button>
                <Button type="submit" disabled={isLoading || !hasChanges}>
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </Button>
              </div>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
